import React, { lazy, ReactElement, Suspense, useEffect } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import ProtectedRoutes from './protected';
import storage from '@utils/storage';
import { FullPageSpinner } from '@components/Common/Spinner/FullPageSpinner';
import UserList from '@pages/Users/UserList';
import AgentList from '@pages/Agents/AgentList';
import AgentAnalytics from '@pages/Agents/AgentAnalytics';
import ErrorLogs from '@pages/ErrorLogs/ErrorLogs';
import VapiKeys from '@pages/VapiKeys/VapiKeys';
import Profile from '@pages/Profile/Profile';
import Plans from '@pages/Subscriptions/Plans';
import UserSubscriptions from '@pages/Subscriptions/UserSubscriptions';
import Addons from '@pages/Subscriptions/Addons';
import RefundRequests from '@pages/Subscriptions/RefundRequests';

export const Unauthorized = lazy(() => import('@pages/Unauthorized/Unauthorized'));
export const Login = lazy(() => import('@pages/Login/Login'));
export const ForgotPassword = lazy(() => import('@pages/ForgotPassword/ForgotPassword'));

export const NotificationList = lazy(() => import('@pages/Notification/NotificationList'));
export const Dashboard = lazy(() => import('@pages/Dashboard/Dashboard'));

interface Routes {
  path: string;
  element: ReactElement;
  roles?: string[];
}

const authRoutes: Routes[] = [
  {
    path: '/auth/login',
    element: <Login />,
  },
  {
    path: '/auth/forgot-password',
    element: <ForgotPassword />,
  },
];

const protectedRoutes: Routes[] = [
  {
    path: '/app/dashboard',
    element: <Dashboard />,
  },
  {
    path: '/app/users',
    element: <UserList />,
  },
  {
    path: '/app/agents',
    element: <AgentList />,
  },
  {
    path: '/app/agents/analytics',
    element: <AgentAnalytics />,
  },
  {
    path: '/app/error-logs',
    element: <ErrorLogs />,
  },
  {
    path: '/app/vapi-keys',
    element: <VapiKeys />,
  },
  {
    path: '/app/subscriptions/plans',
    element: <Plans />,
  },
  {
    path: '/app/subscriptions/users',
    element: <UserSubscriptions />,
  },
  {
    path: '/app/subscriptions/addons',
    element: <Addons />,
  },
  {
    path: '/app/subscriptions/refund-requests',
    element: <RefundRequests />,
  },
  {
    path: '/app/notification',
    element: <NotificationList />,
  },
  {
    path: '/app/profile',
    element: <Profile />,
  },
];

export default function AppRoutes() {
  const navigate = useNavigate();
  const location = useLocation();
  const isUserloggedIn = !!storage.getToken();
  // const navigatePath = isUserloggedIn ? '/app/dashboard' : '/auth/login';
  const navigatePath = isUserloggedIn ? '/app/users' : '/auth/login';

  useEffect(() => {
    if (![...protectedRoutes, ...authRoutes].map((item) => item.path).includes(location.pathname)) {
      navigate(navigatePath);
    }
  }, [isUserloggedIn]);

  return (
    <Routes>
      <Route>
        {authRoutes.map((route, index) => (
          <Route
            key={index}
            path={route.path}
            element={isUserloggedIn ? <Navigate to={'/app/users'} /> : route.element}
          // element={isUserloggedIn ? <Navigate to={'/app/dashboard'} /> : route.element}
          />
        ))}
        <Route path="/auth" element={<Navigate to={navigatePath} />} />
      </Route>
      {isUserloggedIn ? (
        <Route element={<ProtectedRoutes />}>
          {protectedRoutes.map((route, index) => (
            <Route
              key={index}
              path={route.path}
              element={
                isUserloggedIn ? (
                  <Suspense fallback={<FullPageSpinner />}>{route.element}</Suspense>
                ) : (
                  <Navigate to={'/auth/login'} replace={true} />
                )
              }
            />
          ))}
          <Route path="/app" element={<Navigate to={navigatePath} />} />
        </Route>
      ) : null}
      <Route path="/unauthorized" element={<Unauthorized />} />
      <Route path="*" element={<Navigate to={navigatePath} />} />
    </Routes>
  );
}
